import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';

import { Icons, type IconName } from '@/assets/icons/icons';
import { type AxonDevice } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  integrationsLabel,
  platformOf,
  useAxonDevicesQuery,
  useRevokeDevice,
} from '@/features/integration-management/devices.query';

const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const PLATFORM_ICONS: Record<string, IconName> = {
  macos: 'laptop',
  windows: 'monitor',
  ios: 'smartphone',
  android: 'smartphone',
};

export const YourDevicesSection: React.FC = () => {
  const { data: devices } = useAxonDevicesQuery();
  const { revoke, revokingSlug } = useRevokeDevice();

  if (!devices?.some((device) => !device.is_this_device)) return null;

  return (
    <YStack className="gap-4">
      <h3 className="text-sm font-medium text-foreground-muted">
        Your devices
      </h3>
      <div className="grid grid-cols-2 gap-5">
        {devices.map((device) => (
          <DeviceRow
            key={device.device_slug}
            device={device}
            isRevoking={revokingSlug === device.device_slug}
            onRevoke={() => void revoke(device)}
          />
        ))}
      </div>
    </YStack>
  );
};

const DeviceRow = ({
  device,
  isRevoking,
  onRevoke,
}: {
  device: AxonDevice;
  isRevoking: boolean;
  onRevoke: () => void;
}) => {
  const platform = platformOf(device.platform);
  const Icon = Icons[PLATFORM_ICONS[device.platform] ?? 'monitor'];
  const lastActive = new Date(device.last_active_at);
  const relative = formatDistanceToNowStrict(lastActive, { addSuffix: true });
  const isStale = Date.now() - lastActive.getTime() > STALE_AFTER_MS;
  const summary = `${platform.label} · ${integrationsLabel(device.integration_count)} · `;
  return (
    <XStack
      data-device-row
      className="items-center gap-4 rounded-2xl p-2 transition-colors hover:bg-surface-hover"
    >
      <div className="size-12 rounded-lg border border-border bg-surface flex items-center justify-center shrink-0">
        <Icon className="size-6 text-foreground-muted" />
      </div>
      <YStack className="min-w-0 flex-1">
        <XStack className="items-center gap-2 min-w-0">
          <span className="font-medium text-foreground truncate min-w-0">
            {device.device_slug}
          </span>
          {device.is_this_device ? (
            <span className="text-xs rounded-full px-2 py-0.5 font-medium shrink-0 text-foreground-muted bg-surface-active">
              This device
            </span>
          ) : null}
        </XStack>
        {isStale ? (
          <p className="truncate text-sm text-foreground-muted">
            {summary}
            <span className="text-[color-mix(in_srgb,var(--warning-11)_55%,var(--foreground-muted))]">{`Last active ${relative}`}</span>
          </p>
        ) : (
          <p className="truncate text-sm text-foreground-muted">
            {`${summary}Active ${relative}`}
          </p>
        )}
      </YStack>
      {device.is_this_device ? null : (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRevoke}
          disabled={isRevoking}
          aria-label={`Revoke ${device.device_slug}`}
          className="shrink-0 text-foreground-muted hover:text-foreground"
        >
          Revoke
        </Button>
      )}
    </XStack>
  );
};
